import { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@c2fo/liquidity';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { useReporting } from '@/reporting';
import { useIntercom } from '@/reporting/Intercom';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { Navigation as NavigationCore, NavigationItem } from './core/Navigation';
import { useNavigationItems, useNavigationContext } from './NavigationContext';
import { useReactRouterRenderProps } from './reactRouterRenderers';

interface NavigationProps {
  className?: string;
  isAutoWidth?: boolean;
  onItemChange?: () => void;
  portalContainer?: Element | null;
  shouldUsePortal?: boolean;
  style?: React.CSSProperties;
  variant?: 'desktop' | 'mobile';
}

const Navigation = ({
  className,
  isAutoWidth,
  onItemChange,
  portalContainer,
  shouldUsePortal,
  style,
  variant = 'desktop',
}: NavigationProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { openChat } = useIntercom();
  const navigationItems = useNavigationItems();
  const enableUnification = useEnableUnification();
  const logoHeaderConfig = {
    as: Link,
    to: enableUnification ? asEnterpriseLink('homepage') : asEnterpriseLink('customers'),
  };
  const { trackEnterpriseEvent } = useReporting();
  const { renderItem, renderSubItem } = useReactRouterRenderProps();
  const {
    isNavCollapsed,
    setIsNavCollapsed,
    isNavCollapseAllowed,
    initiateExternalRedirect,
    activeItemId,
    setActiveItemId,
    setIsSubNavOpen,
    setSelectedPrimaryNavItem,
    isSubNavOpen,
    selectedPrimaryNavItem,
  } = useNavigationContext();

  // find the currently active item based on the current path
  const getActiveItemInfo = useCallback(() => {
    const currentPath = location.pathname;

    // return offer-activity as the active subitem for customers route when homepage is enabled
    if (enableUnification && currentPath.startsWith('/enterprise/supplier/customers')) {
      for (const item of navigationItems) {
        if (item.subItems) {
          const offerActivitySubItem = item.subItems.find((sub) => sub.id === 'offer-activity');
          if (offerActivitySubItem) {
            return { activeItemId: 'offer-activity', parentItem: item };
          }
        }
      }

      return { activeItemId: 'offer-activity', parentItem: undefined };
    }

    // return markets as the active subitem for markets route since it is not a top level navigation item
    if (currentPath.startsWith('/enterprise/supplier/markets')) {
      for (const item of navigationItems) {
        if (item.subItems) {
          const marketsSubItem = item.subItems.find((sub) => sub.id === 'markets');
          if (marketsSubItem) {
            return { activeItemId: 'markets', parentItem: item };
          }
        }
      }

      return { activeItemId: 'markets', parentItem: undefined };
    }

    // return build-offer active sub-item since /offer/ is not a top level navigation item
    // trailing slash is required to avoid matching other routes with "/offer"
    // this route is used for draft offers
    if (currentPath.startsWith('/enterprise/supplier/offer/')) {
      for (const item of navigationItems) {
        if (item.subItems) {
          const buildOfferSubItem = item.subItems.find((sub) => sub.id === 'build-offer');
          if (buildOfferSubItem) {
            return { activeItemId: 'build-offer', parentItem: item };
          }
        }
      }

      return { activeItemId: 'build-offer', parentItem: undefined };
    }

    // Invoice Central subitem routes are set to /eligible, but /ineligible and /adjustments for that buyer/currency combination also fall under the same subitem
    if (currentPath.startsWith('/enterprise/supplier/invoice-central')) {
      for (const item of navigationItems) {
        if (item.subItems) {
          const currentBuyerCurrency = currentPath.split('/')[4] + '-' + currentPath.split('/')[5];
          const invoiceCentralSubItem = item.subItems.find(
            (sub) => sub.id === `invoice-central-${currentBuyerCurrency}`
          );
          if (invoiceCentralSubItem) {
            return { activeItemId: invoiceCentralSubItem.id, parentItem: item };
          }
        }
      }

      return { activeItemId: undefined, parentItem: undefined };
    }

    // Extended Network Finance child routes
    if (currentPath.startsWith('/enterprise/supplier/extended-network-finance/')) {
      for (const item of navigationItems) {
        if (item.id === 'extended-network-finance' && item.subItems && item.subItems.length > 0) {
          // Look for exact match (we're guaranteed to be on a child route due to the trailing slash check above)
          for (const subItem of item.subItems) {
            if (subItem.path && currentPath === subItem.path) {
              return { activeItemId: subItem.id, parentItem: item };
            }
          }

          // If we found the ENF item with subItems but no matching subItem, don't fall through to generic matching
          // This prevents the parent item from being selected when on a child route
          break;
        }
      }
    }

    // check for direct item matches first
    for (const item of navigationItems) {
      if (item.path && currentPath.startsWith(item.path)) {
        return { activeItemId: item.id, parentItem: undefined };
      }

      // check sub-items
      if (item.subItems) {
        for (const subItem of item.subItems) {
          if (subItem.path && currentPath.startsWith(subItem.path)) {
            return { activeItemId: subItem.id, parentItem: item };
          }
        }
      }
    }

    return { activeItemId: undefined, parentItem: undefined };
  }, [location.pathname, navigationItems, enableUnification]);

  // sync active item and sub-nav state based on current route
  useEffect(() => {
    const { activeItemId: currentActiveItemId, parentItem } = getActiveItemInfo();

    // update active item if it changed
    if (currentActiveItemId && currentActiveItemId !== activeItemId) {
      setActiveItemId(currentActiveItemId);
    }

    // handle sub-navigation state
    if (parentItem) {
      // current route is a sub-item - set parent and open sub-nav (if not collapsed)
      setSelectedPrimaryNavItem(parentItem);
      if (!isNavCollapsed) {
        setIsSubNavOpen(true);
      }
    } else {
      // current route is not a sub-item - clear state and close sub-nav
      setSelectedPrimaryNavItem(null);
      setIsSubNavOpen(false);
    }
  }, [getActiveItemInfo, activeItemId, setActiveItemId, setSelectedPrimaryNavItem, setIsSubNavOpen, isNavCollapsed]);

  const handleExternalRedirect = useCallback(
    (redirectKey: string) => {
      trackEnterpriseEvent('navigation::clicked', {
        item: redirectKey,
        linkType: 'external',
      });
      initiateExternalRedirect(redirectKey);
    },
    [trackEnterpriseEvent, initiateExternalRedirect]
  );

  const handleToggleCollapsed = () => {
    if (isNavCollapseAllowed) {
      setIsNavCollapsed(!isNavCollapsed);
    }
  };

  const handleItemChange = useCallback(
    (activeItem: NavigationItem) => {
      setActiveItemId(activeItem.id);
      trackEnterpriseEvent('navigation::clicked', {
        item: activeItem.id,
        linkType: 'internal',
      });
      onItemChange?.();
    },
    [setActiveItemId, onItemChange, trackEnterpriseEvent]
  );

  return (
    <div className={cn(className)} style={style}>
      <NavigationCore
        chat={{
          chatText: t('navigation.chat'),
          onClick: () => openChat(),
        }}
        header={logoHeaderConfig}
        initialActiveItemId={activeItemId}
        activeItemId={activeItemId}
        isCollapsed={isNavCollapsed}
        items={navigationItems}
        onExternalRedirect={handleExternalRedirect}
        onToggleCollapsed={isNavCollapseAllowed ? handleToggleCollapsed : undefined}
        renderItem={renderItem}
        renderSubItem={renderSubItem}
        isAutoWidth={isAutoWidth}
        onItemChange={handleItemChange}
        variant={variant}
        portalContainer={portalContainer}
        shouldUsePortal={shouldUsePortal}
        externalIsSubNavOpen={isSubNavOpen}
        externalSelectedItemId={selectedPrimaryNavItem?.id}
        onSubNavOpenChange={setIsSubNavOpen}
        onSelectedItemChange={(id) => {
          const item = navigationItems.find((i) => i.id === id);
          setSelectedPrimaryNavItem(item ?? null);
        }}
      />
    </div>
  );
};

export default Navigation;
